import { IKeyValuePair } from '../../../utils/constants/interfaces';
import Container from '../Container/Container';
import styles from './Multiselect.module.css';
import useMultiselect from './useMultiselect';

interface IMultiselectProps {
  visible?: boolean;
  className?: string;
  size?: 'small' | 'default' | 'large';
  noneSelectedLabel?: string;
  allSelectedLabel?: string;
  maxSelectedDisplayCount?: number;
  maxSelectedItemWidth?: number;
  trimDisplayNameAfterChar?: string;
  options: IKeyValuePair<string>[];
  selectedOptions?: string[];
  onChange?: (values: string[]) => void;
}

export const Multiselect = ({
  visible = true,
  className = '',
  size = 'default',
  noneSelectedLabel = 'Nothing selected',
  allSelectedLabel = 'All selected',
  maxSelectedDisplayCount = 3,
  maxSelectedItemWidth = 100,
  trimDisplayNameAfterChar = '',
  options,
  selectedOptions = [],
  onChange = () => {}
}: IMultiselectProps) => {
  const manager = useMultiselect(options, selectedOptions, onChange);

  if (!visible) return <></>;

  let sizeModClass = '';
  if (size === 'small') {
    sizeModClass = 'form-select-sm';
  } else if (size === 'large') {
    sizeModClass = 'form-select-lg';
  }

  const buildSelectedDisplay = (): JSX.Element | JSX.Element[] => {
    if (manager.selectedValues.length > 0 && options.length === manager.selectedValues.length) {
      return <span>{allSelectedLabel}</span>;
    } else if (manager.selectedValues.length === 0) {
      return <span>{noneSelectedLabel}</span>;
    }

    const displayElems: JSX.Element[] = [];
    const overLimitNames: string[] = [];

    for (let i = 0; i < manager.selectedValues.length; i++) {
      // Find the option to get the display value
      const selectedKey = manager.selectedValues[i];
      const option = options.find((opt) => opt.key === selectedKey);
      let value = option ? option.value : selectedKey;

      if (trimDisplayNameAfterChar !== '' && value.indexOf(trimDisplayNameAfterChar) !== -1) {
        value = value.split(trimDisplayNameAfterChar)[0];
      }

      if (i < maxSelectedDisplayCount) {
        displayElems.push(
          <span
            key={value}
            title={value}
            className={styles.selectedValue}
            style={{ maxWidth: maxSelectedItemWidth }}>
            {value}
          </span>
        );
      } else {
        overLimitNames.push(value);
      }
    }

    if (manager.selectedValues.length > maxSelectedDisplayCount) {
      const remainingCount = manager.selectedValues.length - maxSelectedDisplayCount;
      displayElems.push(
        <span
          key="overLimitNames"
          className={styles.selectedValue}
          title={overLimitNames.join(' ')}>
          +{remainingCount} more
        </span>
      );
    }
    return displayElems;
  };

  return (
    <div ref={manager.componentRef} className={`${styles.multiSelectContainer} ${className}`}>
      <div
        className={`form-select ${sizeModClass} ${styles.multiSelect}`}
        onClick={() => manager.toggleOptionDisplay()}>
        {buildSelectedDisplay()}
      </div>
      <Container visible={manager.showOptions} className={styles.multiSelectOptionPanel}>
        <div className={styles.multiSelectHeader}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              manager.selectAllOptions();
            }}>
            Select All
          </a>
          <span className={styles.headerSpacer}> | </span>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              manager.deselectAllOptions();
            }}>
            Select None
          </a>
        </div>
        <div className={styles.multiSelectOptions}>
          <ul>
            {options.map((option) => {
              return (
                <li key={option.key}>
                  <div className="form-check">
                    <label className="form-check-label">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        onChange={() => manager.toggleOptionSelect(option.key)}
                        checked={manager.isSelected(option.key)}
                      />
                      {option.value}
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <a
            href="#"
            className={`btn btn-primary ${styles.closeButton}`}
            onClick={(e) => {
              e.preventDefault();
              manager.toggleOptionDisplay();
            }}>
            close
          </a>
        </div>
      </Container>
    </div>
  );
};
